import {
  type AppleNativeSignIn,
  apiErrorSchema,
  authResponseSchema,
  CLIENT_PLATFORM_HEADER,
  type ClearDataInput,
  type ClientPlatform,
  type DeleteAccountInput,
  type ForgotPasswordInput,
  type GoogleCalendarPhoneFinish,
  googleCalendarPhoneStartSchema,
  googleCalendarStatusSchema,
  googleCalendarSyncSchema,
  type LoginInput,
  type Mutation,
  meResponseSchema,
  metaResponseSchema,
  type NativeSignInExchange,
  type NotificationPlan,
  type PushDevice,
  pullResponseSchema,
  pushKeyResponseSchema,
  pushResponseSchema,
  pushTestResponseSchema,
  type RegisterInput,
  type ResetPasswordInput,
  type UpdateProfileInput,
  type VerifyEmailInput,
} from '@dovefiniscono/shared';
import type { ZodType } from 'zod';

export type ApiClientOptions = {
  baseUrl: string;
  /** `web` relies on the HttpOnly cookie, `native` sends the stored bearer token. */
  platform: ClientPlatform;
  getToken?: () => Promise<string | null>;
  fetch?: typeof fetch;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** The request never reached the API (offline, DNS, timeout). */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Connessione non disponibile.', { cause });
    this.name = 'NetworkError';
  }
}

type RequestOptions<T> = { body?: unknown; schema?: ZodType<T> };

export function createApiClient(options: ApiClientOptions) {
  const doFetch = options.fetch ?? fetch;

  async function request<T = void>(
    method: string,
    path: string,
    { body, schema }: RequestOptions<T> = {},
  ): Promise<T> {
    const headers: Record<string, string> = { [CLIENT_PLATFORM_HEADER]: options.platform };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (options.platform === 'native') {
      const token = await options.getToken?.();
      if (token) headers.authorization = `Bearer ${token}`;
    }

    let response: Response;
    try {
      const init = {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        credentials: options.platform === 'web' ? 'include' : 'omit',
      };
      // A plain object: `credentials` is unknown to the Workers typings this file is also checked with.
      response = await doFetch(`${options.baseUrl}${path}`, init as RequestInit);
    } catch (error) {
      throw new NetworkError(error);
    }

    if (!response.ok) {
      const parsed = apiErrorSchema.safeParse(await response.json().catch(() => null));
      if (parsed.success) {
        const { code, message, details } = parsed.data.error;
        throw new ApiError(response.status, code, message, details);
      }
      throw new ApiError(response.status, 'internal_error', 'Risposta inattesa dal server.');
    }

    if (!schema) return undefined as T;
    return schema.parse(await response.json());
  }

  return {
    meta: () => request('GET', '/v1/meta', { schema: metaResponseSchema }),
    auth: {
      register: (input: RegisterInput) =>
        request('POST', '/v1/auth/register', { body: input, schema: authResponseSchema }),
      login: (input: LoginInput) =>
        request('POST', '/v1/auth/login', { body: input, schema: authResponseSchema }),
      appleNative: (input: AppleNativeSignIn) =>
        request('POST', '/v1/auth/oauth/apple/native', { body: input, schema: authResponseSchema }),
      nativeExchange: (input: NativeSignInExchange) =>
        request('POST', '/v1/auth/oauth/native/exchange', {
          body: input,
          schema: authResponseSchema,
        }),
      logout: () => request('POST', '/v1/auth/logout'),
      verifyEmail: (input: VerifyEmailInput) =>
        request('POST', '/v1/auth/email/verify', { body: input, schema: meResponseSchema }),
      resendVerification: () => request('POST', '/v1/auth/email/resend'),
      forgotPassword: (input: ForgotPasswordInput) =>
        request('POST', '/v1/auth/password/forgot', { body: input }),
      resetPassword: (input: ResetPasswordInput) =>
        request('POST', '/v1/auth/password/reset', { body: input }),
    },
    me: {
      get: () => request('GET', '/v1/me', { schema: meResponseSchema }),
      update: (input: UpdateProfileInput) =>
        request('PATCH', '/v1/me', { body: input, schema: meResponseSchema }),
      delete: (input: DeleteAccountInput) => request('DELETE', '/v1/me', { body: input }),
      /** Empties the account and keeps it: devices start over at their next sync. */
      clearData: (input: ClearDataInput) => request('POST', '/v1/me/data/clear', { body: input }),
    },
    notifications: {
      key: () => request('GET', '/v1/notifications/key', { schema: pushKeyResponseSchema }),
      addDevice: (device: PushDevice) =>
        request('POST', '/v1/notifications/devices', { body: device }),
      removeDevice: (endpoint: string) =>
        request('DELETE', '/v1/notifications/devices', { body: { endpoint } }),
      setPlan: (plan: NotificationPlan) => request('PUT', '/v1/notifications/plan', { body: plan }),
      test: () => request('POST', '/v1/notifications/test', { schema: pushTestResponseSchema }),
    },
    sync: {
      pull: (since: number) =>
        request('GET', `/v1/sync/pull?since=${since}`, { schema: pullResponseSchema }),
      push: (mutations: Mutation[]) =>
        request('POST', '/v1/sync/push', { body: { mutations }, schema: pushResponseSchema }),
    },
    integrations: {
      googleCalendar: {
        status: () =>
          request('GET', '/v1/integrations/google-calendar', {
            schema: googleCalendarStatusSchema,
          }),
        /** Imports Google's changes and sends ours; `more` says another round is needed. */
        sync: () =>
          request('POST', '/v1/integrations/google-calendar/sync', {
            schema: googleCalendarSyncSchema,
          }),
        update: (input: { exportEvents: boolean }) =>
          request('PATCH', '/v1/integrations/google-calendar', {
            body: input,
            schema: googleCalendarStatusSchema,
          }),
        disconnect: () =>
          request('DELETE', '/v1/integrations/google-calendar', {
            schema: googleCalendarStatusSchema,
          }),
        /** Where the browser goes to give access to Google Calendar (a full-page navigation). */
        startUrl: (returnTo = '/calendario') =>
          `${options.baseUrl}/v1/integrations/google-calendar/start?return_to=${encodeURIComponent(returnTo)}`,
        /** The phone's browser has no session: the app asks for Google's page with its own. */
        phoneStart: () =>
          request('POST', '/v1/integrations/google-calendar/native/start', {
            schema: googleCalendarPhoneStartSchema,
          }),
        /** Finishes with what the browser brought back. */
        phoneFinish: (input: GoogleCalendarPhoneFinish) =>
          request('POST', '/v1/integrations/google-calendar/native/finish', {
            body: input,
            schema: googleCalendarStatusSchema,
          }),
      },
    },
    /** Where the browser goes to sign in with Google or Apple (a full-page navigation). */
    oauthStartUrl: (provider: 'google' | 'apple', returnTo = '/') =>
      `${options.baseUrl}/v1/auth/oauth/${provider}/start?return_to=${encodeURIComponent(returnTo)}`,
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
